import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  ModalProps,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import {
  EntityIdentifier,
  EntityObject,
  getObjectId,
  getObjectType,
  matchesTagDefinition,
  normalizeTagDefinitionKey,
  normalizeTagInput,
  TagDefinitionEntity,
  tagDefinitionSourceKind,
  tagDefinitionsSelectorWithParams,
  useGetAllTagDefinitionsQuery,
} from "@ots-frontend/ots-data";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {IconPlus, IconSearch, IconX} from "@tabler/icons-react";
import {useTagDefinitionMutation} from "../../../hooks/useTagDefinitionMutation";
import {useTagAssignmentMutation} from "../../../hooks/useTagAssignmentMutation";

export type BulkObjectTagManagerModalProps = Omit<ModalProps, "title"> & {
  entities: EntityObject[];
};

type EditMode = "add" | "remove";

export const BulkObjectTagManagerModal = (props: BulkObjectTagManagerModalProps) => {
  const {entities, opened, onClose, ...rest} = props;

  const {t} = useTranslation(["common"]);

  const [query, setQuery] = useState("");
  const [editMode, setEditMode] = useState<EditMode>("add");
  const [addTagIds, setAddTagIds] = useState<string[]>([]);
  const [removeTagIds, setRemoveTagIds] = useState<string[]>([]);

  const {
    createTagDefinition,
    isCreating: isCreatingTagDefinition,
  } = useTagDefinitionMutation();

  const {
    bulkSaveTagAssignments,
    isUpdating: isBulkSaving,
  } = useTagAssignmentMutation();

  const selectTagDefinitions = useMemo(() => {
    return tagDefinitionsSelectorWithParams();
  }, []);

  const {tagDefinitions, isLoadingTagDefinitions} = useGetAllTagDefinitionsQuery(undefined, {
    selectFromResult: (result) => ({
      tagDefinitions: selectTagDefinitions(result, {doNotUse: false}),
      isLoadingTagDefinitions: result.isLoading,
    }),
  });

  const entityCount = entities.length;

  const distinctObjectTypes = useMemo(() => {
    return Array.from(
      new Set((entities ?? []).map((entity) => getObjectType(entity)).filter(Boolean))
    ).sort();
  }, [entities]);

  const batchObjects: EntityIdentifier[] = useMemo(() => {
    return (entities ?? []).map((entity) => ({
      id: getObjectId(entity),
      type: getObjectType(entity),
    }));
  }, [entities]);

  const addIdSet = useMemo(() => new Set(addTagIds), [addTagIds]);
  const removeIdSet = useMemo(() => new Set(removeTagIds), [removeTagIds]);

  const selectedTagsToAdd = useMemo(() => {
    const map = new Map((tagDefinitions ?? []).map((t) => [t.id, t]));
    return addTagIds
      .map((id) => map.get(id))
      .filter((t): t is TagDefinitionEntity => !!t)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [addTagIds, tagDefinitions]);

  const selectedTagsToRemove = useMemo(() => {
    const map = new Map((tagDefinitions ?? []).map((t) => [t.id, t]));
    return removeTagIds
      .map((id) => map.get(id))
      .filter((t): t is TagDefinitionEntity => !!t)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [removeTagIds, tagDefinitions]);

  const exactMatch = useMemo(() => {
    const normalizedInput = normalizeTagInput(query);
    const normalizedKey = normalizeTagDefinitionKey(query);

    if (!normalizedInput) {
      return undefined;
    }

    return (tagDefinitions ?? []).find((tag) => {
      return (
        normalizeTagInput(tag.label) === normalizedInput ||
        normalizeTagDefinitionKey(tag.key) === normalizedKey
      );
    });
  }, [tagDefinitions, query]);

  const canCreate = query.trim().length > 0 && !exactMatch;

  const eligibleTagDefinitions = useMemo(() => {
    return [...(tagDefinitions ?? [])]
      .filter((def) => {
        if (editMode === "add") {
          return !addIdSet.has(def.id);
        }
        return !removeIdSet.has(def.id);
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tagDefinitions, editMode, addIdSet, removeIdSet]);

  const filteredTagDefinitions = useMemo(() => {
    return [...eligibleTagDefinitions]
      .filter((def) => matchesTagDefinition(def, query))
      .slice(0, Math.max(0, Math.min(10, eligibleTagDefinitions.length)));
  }, [eligibleTagDefinitions, query]);

  const dirty = addTagIds.length > 0 || removeTagIds.length > 0;

  const resetState = useCallback(() => {
    setQuery("");
    setEditMode("add");
    setAddTagIds([]);
    setRemoveTagIds([]);
  }, []);

  const _onClose = useCallback(() => {
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  const toggleTag = useCallback(
    (tagId: string) => {
      if (editMode === "add") {
        setAddTagIds((prev) => {
          const next = prev.includes(tagId)
            ? prev.filter((id) => id !== tagId)
            : [...prev, tagId];

          return next;
        });

        setRemoveTagIds((prev) => prev.filter((id) => id !== tagId));
        return;
      }

      setRemoveTagIds((prev) => {
        const next = prev.includes(tagId)
          ? prev.filter((id) => id !== tagId)
          : [...prev, tagId];

        return next;
      });

      setAddTagIds((prev) => prev.filter((id) => id !== tagId));
    },
    [editMode]
  );

  const removeFromAdd = useCallback((tagId: string) => {
    setAddTagIds((prev) => prev.filter((id) => id !== tagId));
  }, []);

  const removeFromRemove = useCallback((tagId: string) => {
    setRemoveTagIds((prev) => prev.filter((id) => id !== tagId));
  }, []);

  const handleCreateTagDefinition = useCallback(() => {
    if (!query.trim()) {
      return;
    }

    createTagDefinition(
      {
        label: query.trim(),
        sourceKind: tagDefinitionSourceKind.user.code,
      },
      {
        onSuccess: (def) => {
          if (editMode === "add") {
            setAddTagIds((prev) => (prev.includes(def.id) ? prev : [...prev, def.id]));
            setRemoveTagIds((prev) => prev.filter((id) => id !== def.id));
          } else {
            setRemoveTagIds((prev) => (prev.includes(def.id) ? prev : [...prev, def.id]));
            setAddTagIds((prev) => prev.filter((id) => id !== def.id));
          }

          setQuery("");
        },
      }
    );
  }, [createTagDefinition, editMode, query]);

  const handleSave = useCallback(() => {
    if (!batchObjects.length || (!addTagIds.length && !removeTagIds.length)) {
      return;
    }

    bulkSaveTagAssignments(
      {
        objects: batchObjects,
        addTagDefIds: addTagIds,
        removeTagDefIds: removeTagIds,
      },
      {
        onSuccess: () => {
          _onClose();
        },
      }
    );
  }, [bulkSaveTagAssignments, batchObjects, addTagIds, removeTagIds, _onClose]);

  const isLoading = isLoadingTagDefinitions;

  return (
    <Modal
      {...rest}
      opened={opened}
      onClose={_onClose}
      title={
        <Stack spacing={0}>
          <Text fw={600}>
            {t("common.tagassignment.modal.batch.title", "Manage Tags")}
          </Text>
          <Text size="sm" c="dimmed">
            {t("common.tagassignment.modal.batch.subtitle", "{{count}} selected objects", {
              count: entityCount,
            })}
            {distinctObjectTypes.length > 0 ? ` · ${distinctObjectTypes.join(", ")}` : null}
          </Text>
        </Stack>
      }
      size="lg"
      centered
    >
      <Stack spacing="md">
        {isLoading ? (
          <Group position="center" py="xl">
            <Loader size="sm"/>
          </Group>
        ) : (
          <>
            <Card shadow="xs" padding="lg" radius="md" withBorder>
              <Stack spacing="xs">
                <Text size="sm" fw={500}>
                  {t("common.tagassignment.modal.batch.selectedobjects", "Selected Objects")}
                </Text>

                <Text size="sm" c="dimmed">
                  {t(
                    "common.tagassignment.modal.batch.selectedobjectcount",
                    "{{count}} objects will be updated",
                    {count: entityCount}
                  )}
                </Text>

                {distinctObjectTypes.length > 0 ? (
                  <Group spacing="xs">
                    {distinctObjectTypes.map((type) => (
                      <Badge key={type} size="sm" variant="light">
                        {type}
                      </Badge>
                    ))}
                  </Group>
                ) : null}
              </Stack>
            </Card>

            <Card shadow="xs" padding="lg" radius="md" withBorder>
              <Stack spacing="md">
                <SegmentedControl
                  value={editMode}
                  onChange={(value) => setEditMode(value as EditMode)}
                  data={[
                    {
                      label: t("common.action.add", "Add"),
                      value: "add",
                    },
                    {
                      label: t("common.action.remove", "Remove"),
                      value: "remove",
                    },
                  ]}
                />

                <Box>
                  <Text size="sm" fw={500} mb="xs">
                    {t("common.tagassignment.modal.batch.tagstoadd", "Tags to Add")}
                  </Text>

                  {selectedTagsToAdd.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      {t("common.tagassignment.modal.batch.notagstoadd", "No tags selected to add")}
                    </Text>
                  ) : (
                    <Group spacing="xs">
                      {selectedTagsToAdd.map((tag) => (
                        <Badge
                          key={tag.id}
                          variant="light"
                          color="green"
                          rightSection={
                            <ActionIcon
                              size="xs"
                              variant="transparent"
                              onClick={() => removeFromAdd(tag.id)}
                            >
                              <IconX size={10}/>
                            </ActionIcon>
                          }
                        >
                          {tag.label}
                        </Badge>
                      ))}
                    </Group>
                  )}
                </Box>

                <Box>
                  <Text size="sm" fw={500} mb="xs">
                    {t("common.tagassignment.modal.batch.tagstoremove", "Tags to Remove")}
                  </Text>

                  {selectedTagsToRemove.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      {t(
                        "common.tagassignment.modal.batch.notagstoremove",
                        "No tags selected to remove"
                      )}
                    </Text>
                  ) : (
                    <Group spacing="xs">
                      {selectedTagsToRemove.map((tag) => (
                        <Badge
                          key={tag.id}
                          variant="light"
                          color="red"
                          rightSection={
                            <ActionIcon
                              size="xs"
                              variant="transparent"
                              onClick={() => removeFromRemove(tag.id)}
                            >
                              <IconX size={10}/>
                            </ActionIcon>
                          }
                        >
                          {tag.label}
                        </Badge>
                      ))}
                    </Group>
                  )}
                </Box>
              </Stack>
            </Card>

            <Divider variant="dotted"/>

            <TextInput
              size="xs"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder={t(
                "common.tagassignment.modal.batch.searchplaceholder",
                "Search or create tag..."
              )}
              rightSection={<IconSearch size={16}/>}
              onKeyDown={(event) => {
                if (event.key === "Enter" && canCreate) {
                  event.preventDefault();
                  handleCreateTagDefinition();
                }
              }}
            />

            {canCreate && (
              <Button
                size="xs"
                variant="light"
                leftIcon={<IconPlus size={16}/>}
                onClick={handleCreateTagDefinition}
                loading={isCreatingTagDefinition}
              >
                {t("common.tagassignment.modal.batch.createbutton", "Create tag")} “{query.trim()}”
              </Button>
            )}

            <Box>
              <Group spacing="xs">
                <Text size="sm" fw={500} mb="xs">
                  {editMode === "add"
                    ? t("common.tagassignment.modal.batch.availabletagstoadd", "Available Tags to Add")
                    : t(
                      "common.tagassignment.modal.batch.availabletagstoremove",
                      "Available Tags to Remove"
                    )}
                </Text>
                <Text size="xs" color="dimmed" mb="xs">
                  {filteredTagDefinitions.length} {t("common.word.of", "of")}{" "}
                  {eligibleTagDefinitions.length}
                </Text>
              </Group>

              <ScrollArea mah={320}>
                <Stack spacing="xs">
                  {filteredTagDefinitions.length === 0 && !canCreate ? (
                    <Text size="sm" c="dimmed">
                      {t(
                        "common.tagassignment.modal.batch.notagsfound",
                        "No matching tags found"
                      )}
                    </Text>
                  ) : (
                    filteredTagDefinitions.map((tag, idx) => {
                      const selected =
                        editMode === "add" ? addIdSet.has(tag.id) : removeIdSet.has(tag.id);

                      return (
                        <Box key={tag.id}>
                          <Group
                            position="apart"
                            noWrap
                            p="xs"
                            style={{
                              border: "1px solid var(--mantine-color-gray-3)",
                              borderRadius: 8,
                              cursor: "pointer",
                              background: selected
                                ? "var(--mantine-color-blue-0)"
                                : undefined,
                            }}
                            onClick={() => toggleTag(tag.id)}
                          >
                            <Box style={{flex: 1}}>
                              <Group spacing="xs">
                                <Badge size="sm" variant={selected ? "filled" : "light"}>
                                  {tag.label}
                                </Badge>
                                <Text size="xs" c="dimmed">
                                  #{tag.key}
                                </Text>
                              </Group>
                            </Box>

                            <Button
                              size="xs"
                              variant={selected ? "light" : "filled"}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTag(tag.id);
                              }}
                            >
                              {selected
                                ? t("common.action.remove", "Remove")
                                : editMode === "add"
                                  ? t("common.action.add", "Add")
                                  : t("common.action.select", "Select")}
                            </Button>
                          </Group>

                          {idx !== filteredTagDefinitions.length - 1 ? (
                            <Divider variant="dashed" mt="xs"/>
                          ) : null}
                        </Box>
                      );
                    })
                  )}
                </Stack>
              </ScrollArea>
            </Box>

            <Divider/>

            <Group position="apart" mt="sm">
              <Text size="xs" c="dimmed">
                {t(
                  "common.tagassignment.modal.batch.selectedcountsummary",
                  "{{addCount}} to add · {{removeCount}} to remove",
                  {
                    addCount: addTagIds.length,
                    removeCount: removeTagIds.length,
                  }
                )}
              </Text>

              <Group>
                <Button size="xs" variant="default" onClick={_onClose}>
                  {t("common.action.cancel", "Cancel")}
                </Button>

                <Button
                  size="xs"
                  onClick={handleSave}
                  loading={isBulkSaving}
                  disabled={!dirty || !entityCount}
                >
                  {t("common.action.save", "Save")}
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};
